import { DateTime } from "luxon";
import { ServiceLevel } from "@/pages/_layouts/jobs/types";

interface PrefillDevJobFormParams {
  setValue: (name: any, value: any) => void;
  clearErrors: (...args: any[]) => void;
  setSelectedSectionId: (id: string) => void;
  jobCategories: string[];
  currentUser?: {
    name?: string;
    email?: string;
    sub?: string;
    user_id?: string;
  } | null;
}

/**
 * Developer-specific overrides
 * ---------------------------------
 * To customise the pre-fill behaviour on your machine create a file named
 * `prefillDevJobForm.config.ts` in the same directory as this file.
 * The file should export a **default** object that conforms to the
 * `DevPrefillOverrides` type.  See `prefillDevJobForm.config.example.ts`
 * for a ready-made template.
 *
 * The actual `prefillDevJobForm.config.ts` file is git-ignored so each
 * developer can keep their own preferences without accidentally committing
 * them.
 */

type DevPrefillOverrides = Partial<{
  devSectionId: string;
  serviceLevel: ServiceLevel;
  category: string;
  namePrefix: string;
  deadlineOffsetMonths: number;
  positionType: string;
  applicantGroup: string;
  applyVia: string;
  externalJobUrl: string;
  overview: string;
  facilityId: string;
  facilityName: string;
  compensation: {
    baseMinimum?: number;
    baseMaximum?: number;
    totalAnticipated?: number;
    hideCompensationFromJobBoard?: boolean;
  };
  pointOfContact: {
    name?: string;
    email?: string;
    title?: string;
  };
}>;

// Helper – load overrides if present.  `import.meta.glob` returns an empty
// object when the pattern does not match, avoiding build-time errors when
// the local file does not exist.
// We use the `eager` option so that the import happens synchronously.
const configModules = import.meta.glob<{
  readonly default: DevPrefillOverrides;
}>("./prefillDevJobForm.config.ts", { eager: true });

// There will be at most one match. If none, fall back to an empty object.
const localOverrides: DevPrefillOverrides = Object.values(configModules)[0]
  ? (Object.values(configModules)[0] as any).default
  : {};

// Convenience getter function that falls back to a supplied default value.
const withOverride = <T>(key: keyof DevPrefillOverrides, fallback: T): T => {
  return (localOverrides[key] as T | undefined) ?? fallback;
};

/**
 * Populates the Job Post form with sensible defaults to speed up local development.
 * Wrapped in isDev() checks at the call-site so this code never executes in production builds.
 */
export const prefillDevJobForm = ({
  setValue,
  clearErrors,
  setSelectedSectionId,
  jobCategories,
  currentUser
}: PrefillDevJobFormParams) => {
  // -----------------------------
  // Basic details
  // -----------------------------
  const devSectionId = withOverride("devSectionId", "2b46861f-29cf-44be-ae0e-8d4eb1cae19e");
  setValue("sectionId", devSectionId);
  setSelectedSectionId(devSectionId);

  setValue("serviceLevel", withOverride("serviceLevel", ServiceLevel.ADVANCED));
  setValue("category", withOverride("category", jobCategories[0] || "Head Golf Professional"));

  const namePrefix = withOverride("namePrefix", "Prefill #");
  setValue("name", `${namePrefix} ${Math.floor(Math.random() * 1_000_000)}`);

  // -----------------------------
  // Dates
  // -----------------------------
  const deadlineOffsetMonths = withOverride("deadlineOffsetMonths", 1);
  const deadline = DateTime.now().plus({ months: deadlineOffsetMonths }).toFormat("yyyy-MM-dd");
  setValue("submissionDeadline", { startDate: deadline, endDate: deadline });

  // -----------------------------
  // Position & applicant settings
  // -----------------------------
  setValue("positionType", withOverride("positionType", "Year Round"));
  setValue("applicantGroup", withOverride("applicantGroup", "PGA Professional and General Public"));
  setValue("applyVia", withOverride("applyVia", "Facility Website"));
  setValue("externalJobUrl", withOverride("externalJobUrl", "https://my.pga.com"));

  // -----------------------------
  // Description & requirements (other steps)
  // -----------------------------
  setValue("overview", withOverride("overview", "Sample overview for dev testing."));

  const devFacilityId = withOverride("facilityId", "95e40081-0c65-42e0-a424-5dbd1e22776e");
  const devFacilityName = withOverride("facilityName", "Charlie Yates Golf Course");
  // Some parts of the form store the facility name under different field names
  setValue("facilityId", devFacilityId);
  setValue("facility", devFacilityName);
  setValue("facilityName", devFacilityName);

  // -----------------------------
  // Compensation
  // -----------------------------
  const compensationOverrides = localOverrides.compensation ?? {};
  setValue("compensation.baseMinimum", compensationOverrides.baseMinimum ?? 50_000);
  setValue("compensation.baseMaximum", compensationOverrides.baseMaximum ?? 80_000);
  setValue("compensation.totalAnticipated", compensationOverrides.totalAnticipated ?? 100_000);

  // -----------------------------
  // Contact / Employer
  // -----------------------------

  if (currentUser) {
    // Point of Contact (mirrors ContactInformationStep default for Advanced/Executive)
    const pocOverrides = localOverrides.pointOfContact ?? {};
    setValue("pointOfContact.name", pocOverrides.name ?? currentUser.name ?? "");
    setValue("pointOfContact.email", pocOverrides.email ?? currentUser.email ?? "");
    setValue("pointOfContact.title", pocOverrides.title ?? "Career Consultant");
  } else {
    // Fallback static values (legacy behaviour)
    const pocOverrides = localOverrides.pointOfContact ?? {};
    setValue("pointOfContact.name", pocOverrides.name ?? "Pockets McSpruce");
    setValue("pointOfContact.email", pocOverrides.email ?? "mcooke+pockets@pgahq.com");
    setValue("pointOfContact.title", pocOverrides.title ?? "Career Consultant");
  }

  // Clear any lingering errors after prefill
  clearErrors();
};
